import React from "react"
import PropTypes from "prop-types"
import cx from "classnames"
import { Bell, Search } from "react-feather"
import menuToggleIcon from "../../../assets/images/icons/menu-toggle.svg"
import IconButton from "../IconButton/IconButton"
import s from "./Header.module.scss"

const initialsOf = (name) =>
  String(name ?? "")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0] || "")
    .join("") || "?"

/**
 * Application top bar: sidebar toggle, breadcrumb, and the right-hand cluster
 * (search, notifications, user).
 *
 * Presentational — every interaction is a callback so routing/auth stay in the
 * container.
 *
 * @example
 * <Header
 *   breadcrumbs={[{ label: "ThinkGas", onClick: goProject }, { label: "HES" }]}
 *   user={{ name: "Avdhaan", role: "Superadmin" }}
 *   onToggleSidebar={toggle}
 *   notificationCount={2}
 * />
 */
const Header = ({
  breadcrumbs = [],
  user,
  onToggleSidebar,
  onSearchClick,
  onNotificationsClick,
  notificationCount = 0,
  notificationSlot,
  onUserClick,
  userSlot,
  actions,
  className
}) => (
  <header className={cx(s.header, className)}>
    <div className={s.left}>
      {onToggleSidebar && (
        <button
          type="button"
          className={s.menuBtn}
          onClick={onToggleSidebar}
          aria-label="Toggle navigation"
        >
          <img src={menuToggleIcon} alt="" width={24} height={24} />
        </button>
      )}

      <div className={s.crumbs}>
        <nav aria-label="Breadcrumb">
          <ol className={s.crumbList}>
            {breadcrumbs.map((crumb, index) => {
              const isLast = index === breadcrumbs.length - 1
              return (
                <li
                  key={`${crumb.label}-${index}`}
                  className={cx(s.crumb, { [s.crumbMuted]: !isLast })}
                  aria-current={isLast ? "page" : undefined}
                >
                  {crumb.onClick && !isLast ? (
                    <button type="button" className={s.crumbLink} onClick={crumb.onClick}>
                      {crumb.label}
                    </button>
                  ) : (
                    crumb.label
                  )}
                  {!isLast && (
                    <span className={s.sep} aria-hidden="true">
                      /
                    </span>
                  )}
                </li>
              )
            })}
          </ol>
        </nav>
      </div>
    </div>

    <div className={s.right}>
      {actions}

      {onSearchClick && (
        <IconButton label="Search" onClick={onSearchClick}>
          <Search size={17} />
        </IconButton>
      )}

      {/* An app-supplied bell (with its own panel and routing) wins over the
          plain fallback — same arrangement as `userSlot`. */}
      {notificationSlot ||
        (onNotificationsClick && (
          <span className={s.bell}>
            <IconButton label="Notifications" onClick={onNotificationsClick}>
              <Bell size={17} />
            </IconButton>
            {notificationCount > 0 && <span className={s.dot} aria-hidden="true" />}
          </span>
        ))}

      {/* An app-supplied control (with auth wiring) wins over the plain fallback. */}
      {userSlot ||
        (user && (
          <button
            type="button"
            className={s.user}
            onClick={onUserClick}
            aria-label={`Account: ${user.name}`}
          >
            <span className={s.avatar} aria-hidden="true">
              {initialsOf(user.name)}
            </span>
            <span className={s.userMeta}>
              <span className={s.userName}>{user.name}</span>
              {user.role && <span className={s.userRole}>{user.role}</span>}
            </span>
          </button>
        ))}
    </div>
  </header>
)

Header.propTypes = {
  breadcrumbs: PropTypes.arrayOf(
    PropTypes.shape({ label: PropTypes.node.isRequired, onClick: PropTypes.func })
  ),
  user: PropTypes.shape({ name: PropTypes.string, role: PropTypes.string }),
  onToggleSidebar: PropTypes.func,
  onSearchClick: PropTypes.func,
  onNotificationsClick: PropTypes.func,
  notificationCount: PropTypes.number,
  /** Replaces the built-in bell — used to inject the app's NotificationsBell. */
  notificationSlot: PropTypes.node,
  onUserClick: PropTypes.func,
  /** Replaces the built-in user button — used to inject the app's UserMenu. */
  userSlot: PropTypes.node,
  /** Extra controls inserted before search/bell/user. */
  actions: PropTypes.node,
  className: PropTypes.string
}

export { initialsOf }
export default Header
